import React from "react";

/**
 * Skeleton: a placeholder in the shape of what's coming, with a shimmer
 * running across it (.bam-skel in dashboard.css: a transform-only sweep, so
 * a screen full of them costs a composite, not a repaint). The grey is
 * neutral rather than white-on-dark so the same block reads on both themes.
 *
 *   <Skeleton/>                         a block that fills its container
 *   <Skeleton variant="line" width="60%"/>   a line of text
 *   <Skeleton variant="circle" width={36}/>  an avatar
 *   <Skeleton variant="card"/>          a thumbnail and two lines
 *
 * Pass width/height to size any of them. The real content that replaces a
 * skeleton can take .bam-content-in to fade up rather than snap in.
 */
export function Skeleton({ variant = "block", width, height, radius, ratio, style = {} }) {
  if (variant === "card") return <SkeletonCard ratio={ratio} style={style}/>;
  const line = variant === "line";
  const circle = variant === "circle";
  return (
    <div className="bam-skel" aria-hidden="true"
      style={{
        width: width || (circle ? 36 : "100%"),
        height: height ?? (circle ? width || 36 : line ? 12 : 16),
        borderRadius: radius ?? (circle ? "50%" : 6),
        ...(ratio ? { height: "auto", aspectRatio: ratio } : null),
        ...style,
      }}
    />
  );
}

/** SkeletonCard: a content card: thumbnail, title line, meta line. */
export function SkeletonCard({ height, ratio = "16 / 9", style = {} }) {
  return (
    <div aria-hidden="true"
      style={{
        background: "rgba(128,128,128,0.04)",
        border: "1px solid rgba(128,128,128,0.12)",
        borderRadius: 14,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        ...(height ? { height } : null),
        ...style,
      }}
    >
      {height
        ? <Skeleton height={height - 74} radius={0}/>
        : <Skeleton ratio={ratio} radius={0}/>}
      <div style={{ padding: "12px 14px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
        <Skeleton variant="line" height={13} width="78%"/>
        <Skeleton variant="line" height={10} width="46%"/>
      </div>
    </div>
  );
}

/** SkeletonGrid: renders N skeleton cards in a responsive grid. */
export function SkeletonGrid({ count = 6, cardHeight, min = 220, ratio }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`,
        gap: 16,
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} height={cardHeight} ratio={ratio}/>
      ))}
    </div>
  );
}

/** SkeletonList: rows of text lines for list views. */
export function SkeletonList({ count = 5, avatar = 44 }) {
  return (
    <div aria-hidden="true" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: 12,
            background: "rgba(128,128,128,0.04)",
            border: "1px solid rgba(128,128,128,0.12)",
            borderRadius: 10,
          }}
        >
          {avatar ? <Skeleton variant="circle" width={avatar}/> : null}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
            <Skeleton variant="line" height={13} width="45%"/>
            <Skeleton variant="line" height={11} width="70%"/>
          </div>
        </div>
      ))}
    </div>
  );
}

/** SkeletonPost: a community post: author row, two lines of text, the
 *  action strip. */
export function SkeletonPost({ media = false, style = {} }) {
  return (
    <div aria-hidden="true"
      style={{
        background: "rgba(128,128,128,0.04)",
        border: "1px solid rgba(128,128,128,0.12)",
        borderRadius: 16,
        padding: "18px 20px",
        marginBottom: 14,
        ...style,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 14 }}>
        <Skeleton variant="circle" width={36}/>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
          <Skeleton variant="line" height={12} width="34%"/>
          <Skeleton variant="line" height={9} width="20%"/>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
        <Skeleton variant="line" width="92%"/>
        <Skeleton variant="line" width="64%"/>
      </div>
      {media && <Skeleton ratio="16 / 9" radius={10} style={{ marginBottom: 14 }}/>}
      <div style={{ display: "flex", gap: 14, paddingTop: 12, borderTop: "1px solid rgba(128,128,128,0.12)" }}>
        <Skeleton variant="line" width={44} height={14}/>
        <Skeleton variant="line" width={34} height={14}/>
        <div style={{ flex: 1 }}/>
        <Skeleton variant="line" width={52} height={14}/>
      </div>
    </div>
  );
}
